/**
 * Screen Saver shell (internal component name: AmbientMode).
 *
 * Requires an explicit siteId (caller-resolved). Does not invent global site context.
 * Clock always renders; weather degrades gracefully.
 * Tap/click anywhere wakes; controls use data-ambient-control + stopPropagation.
 */
import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSiteWeather } from '../../hooks/useSiteWeather';
import WeatherGlyph from '../weather/WeatherGlyph';
import { formatAmbientDate, formatAmbientTime, useLocalClock } from './useLocalClock';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,650&family=Manrope:wght@400;500;600;700&display=swap';

const WEATHER_REFETCH_MS = 30 * 60 * 1000;

const glassCardSx = {
  bgcolor: 'rgba(20, 30, 40, 0.52)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: { xs: 2.5, md: 3.5 },
  backdropFilter: 'blur(22px)',
  boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
};

const formatTemp = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))}°`;
};

const formatWind = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))} km/h`;
};

const formatPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Math.round(Number(value))}%`;
};

const weekdayShort = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { weekday: 'short' });
};

/** WeatherAPI local time "YYYY-MM-DD HH:MM" → short hour label. */
const hourLabel = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return '—';
  const match = timeStr.trim().match(/(\d{1,2}):(\d{2})\s*$/);
  if (!match) return timeStr;
  let hour = Number(match[1]);
  if (Number.isNaN(hour)) return timeStr;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}${suffix}`;
};

function weekTempExtents(days) {
  let min = Infinity;
  let max = -Infinity;
  for (const day of days) {
    const lo = Number(day?.lowC);
    const hi = Number(day?.highC);
    if (!Number.isNaN(lo)) min = Math.min(min, lo);
    if (!Number.isNaN(hi)) max = Math.max(max, hi);
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  if (max <= min) max = min + 1;
  const pad = Math.max(0.8, (max - min) * 0.08);
  return { min: min - pad, max: max + pad };
}

function ensureAmbientFonts() {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[data-ambient-fonts="1"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  link.setAttribute('data-ambient-fonts', '1');
  document.head.appendChild(link);
}

/**
 * @param {{
 *   siteId?: string|null,
 *   siteName?: string|null,
 *   onExit?: () => void,
 * }} props
 */
export default function AmbientMode({ siteId = null, siteName = null, onExit }) {
  const id = String(siteId || '').trim();
  const now = useLocalClock(1000);
  const {
    weather,
    isLoading,
    isRefreshing,
    isError,
    errorKind,
    stale,
    refresh,
  } = useSiteWeather(id, {
    enabled: Boolean(id),
    refetchInterval: WEATHER_REFETCH_MS,
    staleTime: WEATHER_REFETCH_MS,
  });

  useEffect(() => {
    ensureAmbientFonts();
  }, []);

  useEffect(() => {
    if (typeof onExit !== 'function') return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onExit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onExit]);

  const locationLabel = useMemo(() => {
    const loc = weather?.location || {};
    const parts = [loc.name, loc.region].filter(Boolean);
    if (parts.length) return parts.join(', ');
    return siteName || (id ? id : 'Signal Bridge');
  }, [weather, siteName, id]);

  const forecastDays = useMemo(() => {
    const all = Array.isArray(weather?.forecast) ? weather.forecast : [];
    return all.slice(0, 7);
  }, [weather]);

  const hourlyPoints = useMemo(() => {
    const all = Array.isArray(weather?.hourly) ? weather.hourly : [];
    return all.slice(0, 12);
  }, [weather]);

  const weekRange = useMemo(() => weekTempExtents(forecastDays), [forecastDays]);

  const handleShellPointer = (event) => {
    if (typeof onExit !== 'function') return;
    // Controls mark themselves; do not exit when interacting with them.
    if (event.target?.closest?.('[data-ambient-control="1"]')) return;
    onExit();
  };

  return (
    <Box
      component="main"
      role="main"
      data-testid="ambient-mode"
      onClick={handleShellPointer}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1600,
        overflow: 'hidden',
        color: '#F4F7FB',
        fontFamily: '"Manrope", system-ui, sans-serif',
        cursor: onExit ? 'pointer' : 'default',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        background: `
          radial-gradient(1200px 700px at 12% 18%, rgba(56, 189, 248, 0.16), transparent 55%),
          radial-gradient(900px 600px at 88% 12%, rgba(251, 191, 36, 0.10), transparent 50%),
          linear-gradient(155deg, #071018 0%, #0b1a24 42%, #102433 100%)
        `,
        userSelect: 'none',
      }}
      aria-label="Screen Saver"
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.045) 0.6px, transparent 0.6px)',
          backgroundSize: '18px 18px',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          height: '100%',
          width: '100%',
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 1.75, sm: 2.25, md: 3.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.25, sm: 1.75, md: 2.25 },
          minHeight: 0,
        }}
      >
        {/* Top: clock + current conditions */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1.1fr) minmax(0, 1fr)',
            },
            gap: { xs: 1.25, md: 3 },
            alignItems: { xs: 'start', md: 'stretch' },
            flexShrink: 0,
          }}
        >
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <Typography
              data-testid="ambient-time"
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 650,
                fontSize: {
                  xs: 'clamp(2.4rem, 12vw, 3.4rem)',
                  sm: 'clamp(3rem, 10vw, 4.4rem)',
                  md: 'clamp(3.8rem, 7vw, 6.2rem)',
                },
                lineHeight: 0.95,
                letterSpacing: '-0.03em',
                color: '#F8FAFC',
              }}
            >
              {formatAmbientTime(now)}
            </Typography>
            <Typography
              data-testid="ambient-date"
              sx={{
                mt: { xs: 0.6, md: 1 },
                fontSize: { xs: '0.9rem', sm: '1.05rem', md: '1.3rem' },
                fontWeight: 500,
                color: 'rgba(226,232,240,0.82)',
                letterSpacing: '0.01em',
              }}
            >
              {formatAmbientDate(now)}
            </Typography>
            <Typography
              data-testid="ambient-site-label"
              sx={{
                mt: { xs: 0.75, md: 1.25 },
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.05rem' },
                fontWeight: 600,
                color: 'rgba(148, 197, 220, 0.95)',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
              title={locationLabel}
            >
              {locationLabel}
            </Typography>
          </Box>

          <Box data-testid="ambient-weather" sx={{ minWidth: 0 }}>
            {!id ? (
              <Typography data-testid="ambient-no-site" sx={{ color: 'rgba(226,232,240,0.7)', fontSize: '1rem' }}>
                No site available for Screen Saver.
              </Typography>
            ) : isLoading ? (
              <Box data-testid="ambient-weather-loading" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={22} sx={{ color: 'rgba(148,197,220,0.9)' }} />
                <Typography sx={{ color: 'rgba(226,232,240,0.7)' }}>Loading weather…</Typography>
              </Box>
            ) : errorKind === 'WEATHER_LOCATION_MISSING' ? (
              <Typography data-testid="ambient-weather-unconfigured" sx={{ color: 'rgba(226,232,240,0.65)', fontSize: '1rem' }}>
                Weather is not configured for this site.
              </Typography>
            ) : isError && !weather ? (
              <Typography data-testid="ambient-weather-error" sx={{ color: 'rgba(252, 211, 177, 0.9)', fontSize: '1rem' }}>
                Weather unavailable right now.
              </Typography>
            ) : weather ? (
              <Box sx={{ ...glassCardSx, px: { xs: 1.5, md: 2.25 }, py: { xs: 1.25, md: 1.75 }, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, md: 1.75 }, minWidth: 0 }}>
                  <WeatherGlyph
                    conditionIcon={weather?.current?.conditionIcon}
                    data-testid="ambient-weather-icon"
                    sx={{ fontSize: { xs: 40, sm: 52, md: 58 } }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      data-testid="ambient-temp"
                      sx={{
                        fontFamily: '"Fraunces", Georgia, serif',
                        fontWeight: 650,
                        fontSize: { xs: '2.4rem', sm: '3rem', md: '3.4rem' },
                        lineHeight: 0.95,
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {formatTemp(weather?.current?.temperatureC)}
                    </Typography>
                    <Typography
                      data-testid="ambient-condition"
                      sx={{
                        mt: 0.35,
                        fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.15rem' },
                        fontWeight: 600,
                        color: 'rgba(241,245,249,0.92)',
                      }}
                    >
                      {weather?.current?.condition || '—'}
                    </Typography>
                  </Box>
                  <IconButton
                    data-ambient-control="1"
                    data-testid="ambient-weather-refresh"
                    aria-label="Refresh weather"
                    onClick={(e) => {
                      e.stopPropagation();
                      refresh();
                    }}
                    disabled={isRefreshing}
                    sx={{
                      ml: 'auto',
                      color: 'rgba(226,232,240,0.75)',
                      display: { xs: 'none', sm: 'inline-flex' },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    {isRefreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                  </IconButton>
                </Box>

                <Box
                  data-testid="ambient-weather-details"
                  sx={{
                    mt: { xs: 1.1, md: 1.35 },
                    pt: { xs: 1, md: 1.15 },
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: { xs: 0.75, md: 1.25 },
                  }}
                >
                  <Metric label="High / Low" value={`${formatTemp(weather?.today?.highC)} / ${formatTemp(weather?.today?.lowC)}`} />
                  <Metric label="Precip" value={formatPct(weather?.today?.precipitationChance)} />
                  <Metric
                    label="Wind"
                    value={`${formatWind(weather?.current?.windKph)}${
                      weather?.current?.windDirection ? ` ${weather.current.windDirection}` : ''
                    }`}
                  />
                </Box>

                {stale ? (
                  <Typography
                    data-testid="ambient-weather-stale"
                    sx={{ mt: 0.75, fontSize: '0.75rem', color: 'rgba(252, 211, 177, 0.85)' }}
                  >
                    Updated earlier · data may be stale
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Mid: hourly + weekly fill remaining vertical space */}
        {weather && !isLoading ? (
          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.25, sm: 1.75, md: 2.25 },
            }}
          >
            {hourlyPoints.length > 0 ? (
              <Box
                data-testid="ambient-hourly"
                sx={{
                  ...glassCardSx,
                  px: { xs: 1.25, md: 2 },
                  py: { xs: 1.1, md: 1.5 },
                  minWidth: 0,
                  flexShrink: 0,
                }}
              >
                <SectionLabel>Next hours</SectionLabel>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: 0.25, sm: 0.5 },
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {hourlyPoints.map((hour, index) => {
                    const showOnXs = index < 6;
                    const showOnSm = index < 8;
                    const precip = hour?.precipitationChance;
                    const showPrecip = precip != null && Number(precip) > 0;
                    return (
                      <Box
                        key={hour?.timeEpoch || hour?.time || index}
                        data-testid="ambient-hourly-slot"
                        sx={{
                          flex: {
                            xs: '0 0 auto',
                            md: '1 1 0',
                          },
                          width: { xs: 48, sm: 56, md: 'auto' },
                          minWidth: { md: 0 },
                          textAlign: 'center',
                          py: 0.25,
                          display: {
                            xs: showOnXs ? 'flex' : 'none',
                            sm: showOnSm ? 'flex' : 'none',
                            md: 'flex',
                          },
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.35,
                        }}
                      >
                        <Typography sx={{ fontSize: { xs: '0.68rem', md: '0.72rem' }, fontWeight: 700, color: 'rgba(226,232,240,0.7)' }}>
                          {index === 0 ? 'Now' : hourLabel(hour?.time)}
                        </Typography>
                        <WeatherGlyph
                          conditionIcon={hour?.conditionIcon}
                          sx={{ fontSize: { xs: 22, md: 26 } }}
                        />
                        <Typography sx={{ fontSize: { xs: '0.88rem', md: '0.95rem' }, fontWeight: 700 }}>
                          {formatTemp(hour?.temperatureC)}
                        </Typography>
                        {showPrecip ? (
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'rgba(90, 200, 245, 0.95)' }}>
                            {formatPct(precip)}
                          </Typography>
                        ) : (
                          <Box sx={{ height: '0.65rem' }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}

            {forecastDays.length > 0 ? (
              <Box
                data-testid="ambient-forecast"
                sx={{
                  ...glassCardSx,
                  px: { xs: 1.25, md: 2 },
                  py: { xs: 1.1, md: 1.35 },
                  minWidth: 0,
                  minHeight: 0,
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <SectionLabel>This week</SectionLabel>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-evenly',
                  }}
                >
                  {forecastDays.map((day, index) => {
                    const showOnXs = index < 5;
                    const showOnSm = index < 6;
                    const isToday = index === 0;
                    return (
                      <Box
                        key={day?.date || `${day?.condition}-${index}`}
                        data-testid="ambient-forecast-day"
                        sx={{
                          display: {
                            xs: showOnXs ? 'grid' : 'none',
                            sm: showOnSm ? 'grid' : 'none',
                            md: 'grid',
                          },
                          gridTemplateColumns: '58px 28px 38px minmax(56px, 1fr) 44px',
                          alignItems: 'center',
                          gap: 0.75,
                          py: { xs: 0.35, md: 0.45 },
                          borderTop: index === 0 ? 'none' : '1px solid rgba(255,255,255,0.07)',
                          minHeight: { xs: 28, md: 32 },
                        }}
                      >
                        <Typography sx={{ fontSize: { xs: '0.82rem', md: '0.9rem' }, fontWeight: 700, color: '#F8FAFC' }}>
                          {isToday ? 'Today' : weekdayShort(day?.date)}
                        </Typography>
                        <WeatherGlyph
                          conditionIcon={day?.conditionIcon}
                          sx={{ fontSize: { xs: 20, md: 22 }, justifySelf: 'center' }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: '0.82rem', md: '0.9rem' },
                            fontWeight: 600,
                            color: 'rgba(148,163,184,0.95)',
                            textAlign: 'right',
                          }}
                        >
                          {formatTemp(day?.lowC)}
                        </Typography>
                        <TempRangeBar
                          low={day?.lowC}
                          high={day?.highC}
                          weekMin={weekRange.min}
                          weekMax={weekRange.max}
                          current={weather?.current?.temperatureC}
                          showCurrent={isToday}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: '0.82rem', md: '0.9rem' },
                            fontWeight: 700,
                            textAlign: 'right',
                          }}
                        >
                          {formatTemp(day?.highC)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ flex: '1 1 auto' }} />
        )}

        <Typography
          data-testid="ambient-exit-hint"
          sx={{
            flexShrink: 0,
            alignSelf: 'center',
            fontSize: { xs: '0.65rem', sm: '0.75rem' },
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(148,163,184,0.65)',
            pointerEvents: 'none',
          }}
        >
          Tap or press Esc to exit Screen Saver
        </Typography>
      </Box>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        mb: { xs: 0.6, md: 0.85 },
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'rgba(174, 184, 198, 0.88)',
      }}
    >
      {children}
    </Typography>
  );
}

function TempRangeBar({ low, high, weekMin, weekMax, current, showCurrent }) {
  const lo = Number(low);
  const hi = Number(high);
  const span = weekMax - weekMin || 1;
  const hasRange = !Number.isNaN(lo) && !Number.isNaN(hi);
  const leftPct = hasRange ? ((lo - weekMin) / span) * 100 : 0;
  const rightPct = hasRange ? ((hi - weekMin) / span) * 100 : 100;
  const widthPct = Math.max(rightPct - leftPct, 8);
  const cur = Number(current);
  const currentPct =
    showCurrent && !Number.isNaN(cur) ? Math.min(100, Math.max(0, ((cur - weekMin) / span) * 100)) : null;

  return (
    <Box
      data-testid="ambient-forecast-range"
      sx={{
        position: 'relative',
        height: 4,
        bgcolor: 'rgba(255,255,255,0.16)',
        borderRadius: 2,
        mx: 0.25,
      }}
    >
      {hasRange ? (
        <Box
          sx={{
            position: 'absolute',
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            top: 0,
            bottom: 0,
            borderRadius: 2,
            backgroundImage: 'linear-gradient(90deg, #64D2FF, #70D7AD, #FFD426, #FF9F0A)',
            backgroundSize: `${10000 / widthPct}% 100%`,
            backgroundPosition: `${-(leftPct / widthPct) * 100}% 0`,
            backgroundRepeat: 'no-repeat',
          }}
        />
      ) : null}
      {currentPct != null ? (
        <Box
          data-testid="ambient-forecast-now"
          sx={{
            position: 'absolute',
            left: `${currentPct}%`,
            top: '50%',
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#fff',
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 0 0 2px rgba(15, 23, 32, 0.45)',
          }}
        />
      ) : null}
    </Box>
  );
}

function Metric({ label, value, sx }) {
  return (
    <Box sx={sx}>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(148,163,184,0.85)' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.3,
          fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.05rem' },
          fontWeight: 650,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={String(value)}
      >
        {value}
      </Typography>
    </Box>
  );
}
